/**
 * Family & marriage keyword matrix — deterministic generator (no AI credits).
 * Axes: topic × nationality × city × intent. No prices, no guarantees.
 */
import { FAM_TOPICS } from "./index";

export const FAM_CITIES: readonly { slug: string; th: string }[] = [
  { slug: "bangkok", th: "กรุงเทพ" },
  { slug: "nonthaburi", th: "นนทบุรี" },
  { slug: "pathumthani", th: "ปทุมธานี" },
  { slug: "samutprakan", th: "สมุทรปราการ" },
  { slug: "chonburi", th: "ชลบุรี" },
  { slug: "pattaya", th: "พัทยา" },
  { slug: "rayong", th: "ระยอง" },
  { slug: "chiangmai", th: "เชียงใหม่" },
  { slug: "chiangrai", th: "เชียงราย" },
  { slug: "phuket", th: "ภูเก็ต" },
  { slug: "krabi", th: "กระบี่" },
  { slug: "suratthani", th: "สุราษฎร์ธานี" },
  { slug: "khonkaen", th: "ขอนแก่น" },
  { slug: "udonthani", th: "อุดรธานี" },
  { slug: "nongkhai", th: "หนองคาย" },
  { slug: "ubonratchathani", th: "อุบลราชธานี" },
  { slug: "nakhonratchasima", th: "นครราชสีมา" },
  { slug: "songkhla", th: "สงขลา" },
  { slug: "ayutthaya", th: "อยุธยา" },
  { slug: "huahin", th: "หัวหิน" },
] as const;

export const FAM_NATIONALITIES: readonly { slug: string; th: string; en: string }[] = [
  { slug: "american", th: "อเมริกัน", en: "American" },
  { slug: "british", th: "อังกฤษ", en: "British" },
  { slug: "australian", th: "ออสเตรเลีย", en: "Australian" },
  { slug: "canadian", th: "แคนาดา", en: "Canadian" },
  { slug: "german", th: "เยอรมัน", en: "German" },
  { slug: "french", th: "ฝรั่งเศส", en: "French" },
  { slug: "dutch", th: "เนเธอร์แลนด์", en: "Dutch" },
  { slug: "swedish", th: "สวีเดน", en: "Swedish" },
  { slug: "swiss", th: "สวิส", en: "Swiss" },
  { slug: "italian", th: "อิตาลี", en: "Italian" },
  { slug: "japanese", th: "ญี่ปุ่น", en: "Japanese" },
  { slug: "chinese", th: "จีน", en: "Chinese" },
  { slug: "korean", th: "เกาหลี", en: "Korean" },
  { slug: "indian", th: "อินเดีย", en: "Indian" },
  { slug: "russian", th: "รัสเซีย", en: "Russian" },
  { slug: "singaporean", th: "สิงคโปร์", en: "Singaporean" },
  { slug: "vietnamese", th: "เวียดนาม", en: "Vietnamese" },
  { slug: "filipino", th: "ฟิลิปปินส์", en: "Filipino" },
] as const;

export const FAM_INTENTS: readonly { slug: string; th: string }[] = [
  { slug: "marry-in-thailand", th: "จดทะเบียนสมรสในไทย" },
  { slug: "marry-abroad", th: "จดทะเบียนสมรสในต่างประเทศ" },
  { slug: "spouse-visa", th: "ยื่นวีซ่าคู่สมรส" },
  { slug: "non-o-marriage", th: "ขออยู่ต่อกรณีสมรสกับคนไทย" },
  { slug: "single-status", th: "ขอหนังสือรับรองสถานะโสด" },
  { slug: "record-overseas-marriage", th: "บันทึกฐานะแห่งครอบครัว" },
  { slug: "prenup", th: "ทำสัญญาก่อนสมรส" },
  { slug: "divorce-consent", th: "หย่าโดยความยินยอม" },
  { slug: "divorce-court", th: "ฟ้องหย่า" },
  { slug: "child-custody", th: "ตกลงเรื่องอำนาจปกครองบุตร" },
  { slug: "name-change", th: "เปลี่ยนคำนำหน้าและนามสกุล" },
  { slug: "interpreter-district", th: "หาล่ามไปอำเภอ" },
  { slug: "translate-marriage-cert", th: "แปลทะเบียนสมรส" },
  { slug: "use-abroad", th: "นำเอกสารสมรสไปใช้ต่างประเทศ" },
] as const;

const PREFIXES = ["ขอ", "ยื่น", "บริการ", "ขั้นตอน", "เอกสาร", "ที่ปรึกษา", "ตัวแทนดำเนินการ"] as const;

function build(): string[] {
  const out = new Set<string>();

  for (const t of FAM_TOPICS) {
    out.add(t.th);
    out.add(`${t.th} คืออะไร`);
    out.add(`${t.th} ต้องใช้เอกสารอะไร`);
    out.add(`${t.th} ใช้เวลากี่วันทำการ`);
    out.add(`${t.th} ยื่นที่ไหน`);
    out.add(`${t.th} ถูกตีกลับเพราะอะไร`);
    out.add(t.en.toLowerCase());
    out.add(`${t.en.toLowerCase()} thailand`);
    out.add(`${t.en.toLowerCase()} requirements`);
    for (const p of PREFIXES) out.add(`${p}${t.th}`);
    for (const c of FAM_CITIES) {
      out.add(`${t.th} ${c.th}`);
      out.add(`${t.en.toLowerCase()} ${c.slug}`);
    }
    for (const n of FAM_NATIONALITIES) {
      out.add(`${t.th} คู่สมรสคน${n.th}`);
      out.add(`${t.en.toLowerCase()} for ${n.en.toLowerCase()} spouse`);
    }
    for (const i of FAM_INTENTS) out.add(`${t.th} กรณี${i.th}`);
  }

  for (const i of FAM_INTENTS) {
    out.add(`${i.th} ต้องใช้เอกสารอะไร`);
    out.add(`${i.th} ขั้นตอนทำอย่างไร`);
    out.add(`${i.th} ต้องแปลเอกสารไหม`);
    for (const c of FAM_CITIES) out.add(`${i.th} ${c.th}`);
  }

  for (const c of FAM_CITIES) {
    out.add(`จดทะเบียนสมรสต่างชาติ ${c.th}`);
    out.add(`ล่ามจดทะเบียนสมรส ${c.th}`);
    out.add(`แปลทะเบียนสมรส ${c.th}`);
    out.add(`จดทะเบียนหย่า ${c.th}`);
    out.add(`marriage registration ${c.slug}`);
    out.add(`marriage interpreter ${c.slug}`);
  }

  for (const n of FAM_NATIONALITIES) {
    out.add(`จดทะเบียนสมรสกับคน${n.th}`);
    out.add(`หนังสือรับรองโสด สถานทูต${n.th}`);
    out.add(`marry a thai as ${n.en.toLowerCase()} citizen`);
    out.add(`affirmation of freedom to marry ${n.en.toLowerCase()} embassy bangkok`);
  }

  const LONGTAIL = [
    "จดทะเบียนสมรสกับชาวต่างชาติต้องเตรียมอะไรบ้าง",
    "หนังสือรับรองโสดขอที่ไหน",
    "หนังสือรับรองโสดใช้ได้กี่เดือน",
    "สมรสเท่าเทียมเริ่มใช้เมื่อไหร่",
    "จดทะเบียนสมรสอายุเท่าไหร่ถึงจดได้",
    "สมรสในต่างประเทศต้องแจ้งที่อำเภอไหม",
    "คร.22 คืออะไร",
    "สัญญาก่อนสมรสทำหลังแต่งได้ไหม",
    "หย่าโดยความยินยอมต้องมีพยานกี่คน",
    "ฟ้องหย่าใช้เวลานานไหม",
    "ทะเบียนสมรสไทยใช้ที่ต่างประเทศต้องทำอย่างไร",
    "แปลทะเบียนสมรสรับรองกงสุลใช้เวลากี่วัน",
    "ต้องมีล่ามตอนจดทะเบียนสมรสไหม",
    "เปลี่ยนนามสกุลตามสามีต้องทำอะไรบ้าง",
    "จดทะเบียนสมรสแล้วขอวีซ่าคู่สมรสได้เลยไหม",
  ];
  for (const k of LONGTAIL) out.add(k);

  return Array.from(out);
}

export const FAM_KEYWORDS: string[] = build();
export const FAM_KEYWORD_COUNT = FAM_KEYWORDS.length;
